import React, { useEffect, useRef } from 'react';
import html2pdf from 'html2pdf.js';

interface TransactionData {
  id: string;
  date: string;
  time: string;
  senderCompany: string;
  receiverCompany: string;
  amount: number;
  status: string;
  protocolFee: number;
}

interface PDFData {
  transactions: TransactionData[];
  summary: {
    totalTransactions: number;
    totalAmount: number;
    totalFees: number;
    successRate: string;
  };
  reportDate: string;
  reportTime: string;
}

interface TransactionHistoryPDFProps {
  data: PDFData;
  filename: string;
}

const TransactionHistoryPDF: React.FC<TransactionHistoryPDFProps> = ({ data, filename }) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const generatePDF = async () => {
      if (!contentRef.current) {
        console.error('Content ref not found');
        return;
      }

      // Wait a bit for the content to render
      setTimeout(async () => {
        if (!contentRef.current) {
          console.error('Content ref not found');
          return;
        }

        try {
          // Dynamic import to avoid build issues
          // const html2pdf = (await import('html2pdf.js')).default;
          
          const options = {
            margin: 10,
            filename: filename,
            image: { type: 'jpeg' as const, quality: 0.98 },
            html2canvas: { 
              scale: 2,
              useCORS: true,
              allowTaint: true
            },
            jsPDF: { 
              unit: 'mm', 
              format: 'a4', 
              orientation: 'portrait' as const 
            }
          };

          await html2pdf().set(options).from(contentRef.current).save();
        } catch (error) {
          console.error('Error generating PDF:', error);
        }
      }, 1000);
    };

    generatePDF();
  }, [data, filename]);

  return (
    <div style={{ 
      width: '210mm', 
      minHeight: '297mm', 
      padding: '20mm',
      backgroundColor: 'white',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      lineHeight: '1.4',
      color: '#333',
      margin: '0 auto'
    }}>
      <div ref={contentRef}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px', borderBottom: '3px solid #2563eb', paddingBottom: '20px' }}>
          <h1 style={{ fontSize: '28px', color: '#1e40af', marginBottom: '10px', fontWeight: '700' }}>
            Transaction History Report
          </h1>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            Generated: {data.reportDate} at {data.reportTime}
          </div>
        </div>
        
        {/* Summary */}
        <div style={{ 
          background: '#f8fafc', 
          border: '1px solid #e2e8f0', 
          borderRadius: '8px', 
          padding: '20px', 
          marginBottom: '30px' 
        }}>
          <h2 style={{ fontSize: '18px', color: '#1e40af', marginBottom: '15px', fontWeight: '600' }}>
            Summary
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
            <div style={{ 
              background: 'white', 
              padding: '15px', 
              borderRadius: '6px', 
              borderLeft: '4px solid #2563eb' 
            }}>
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280', 
                textTransform: 'uppercase', 
                fontWeight: '600', 
                letterSpacing: '0.5px' 
              }}>
                Total Transactions
              </div>
              <div style={{ 
                fontSize: '20px', 
                color: '#1f2937', 
                fontWeight: '700', 
                marginTop: '5px' 
              }}>
                {data.summary.totalTransactions}
              </div>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '15px', 
              borderRadius: '6px', 
              borderLeft: '4px solid #2563eb' 
            }}>
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280', 
                textTransform: 'uppercase', 
                fontWeight: '600', 
                letterSpacing: '0.5px' 
              }}>
                Total Amount
              </div>
              <div style={{ 
                fontSize: '20px', 
                color: '#1f2937', 
                fontWeight: '700', 
                marginTop: '5px' 
              }}>
                ${data.summary.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '15px', 
              borderRadius: '6px', 
              borderLeft: '4px solid #2563eb' 
            }}>
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280', 
                textTransform: 'uppercase', 
                fontWeight: '600', 
                letterSpacing: '0.5px' 
              }}>
                Total Fees
              </div>
              <div style={{ 
                fontSize: '20px', 
                color: '#1f2937', 
                fontWeight: '700', 
                marginTop: '5px' 
              }}>
                ${data.summary.totalFees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '15px', 
              borderRadius: '6px', 
              borderLeft: '4px solid #2563eb' 
            }}>
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280', 
                textTransform: 'uppercase', 
                fontWeight: '600', 
                letterSpacing: '0.5px' 
              }}>
                Success Rate
              </div>
              <div style={{ 
                fontSize: '20px', 
                color: '#1f2937', 
                fontWeight: '700', 
                marginTop: '5px' 
              }}>
                {data.summary.successRate}%
              </div>
            </div>
          </div>
        </div>
        
        {/* Transaction Table */}
        <div style={{ 
          background: 'white', 
          borderRadius: '8px', 
          overflow: 'hidden', 
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' 
        }}>
          <div style={{ background: '#1e40af', color: 'white', padding: '15px 20px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600' }}>Transaction Details</h2>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                <th style={{ 
                  color: '#374151', 
                  fontWeight: '600', 
                  padding: '12px 8px', 
                  textAlign: 'left', 
                  borderBottom: '2px solid #e5e7eb', 
                  fontSize: '10px', 
                  textTransform: 'uppercase' 
                }}>
                  Transaction ID
                </th>
                <th style={{ 
                  color: '#374151', 
                  fontWeight: '600', 
                  padding: '12px 8px', 
                  textAlign: 'left', 
                  borderBottom: '2px solid #e5e7eb', 
                  fontSize: '10px', 
                  textTransform: 'uppercase' 
                }}>
                  Date
                </th>
                <th style={{ 
                  color: '#374151', 
                  fontWeight: '600', 
                  padding: '12px 8px', 
                  textAlign: 'left', 
                  borderBottom: '2px solid #e5e7eb', 
                  fontSize: '10px', 
                  textTransform: 'uppercase' 
                }}>
                  From
                </th>
                <th style={{ 
                  color: '#374151', 
                  fontWeight: '600', 
                  padding: '12px 8px', 
                  textAlign: 'left', 
                  borderBottom: '2px solid #e5e7eb', 
                  fontSize: '10px', 
                  textTransform: 'uppercase' 
                }}>
                  To
                </th>
                <th style={{ 
                  color: '#374151', 
                  fontWeight: '600', 
                  padding: '12px 8px', 
                  textAlign: 'left', 
                  borderBottom: '2px solid #e5e7eb', 
                  fontSize: '10px', 
                  textTransform: 'uppercase' 
                }}>
                  Amount
                </th>
                <th style={{ 
                  color: '#374151', 
                  fontWeight: '600', 
                  padding: '12px 8px', 
                  textAlign: 'left', 
                  borderBottom: '2px solid #e5e7eb', 
                  fontSize: '10px', 
                  textTransform: 'uppercase' 
                }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {data.transactions.map((transaction, index) => (
                <tr key={transaction.id} style={{ background: index % 2 === 0 ? '#f9fafb' : 'white' }}>
                  <td style={{ 
                    padding: '8px', 
                    borderBottom: '1px solid #e5e7eb', 
                    fontFamily: 'monospace', 
                    fontSize: '10px', 
                    color: '#6b7280' 
                  }}>
                    {transaction.id.substring(0, 8)}...
                  </td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
                    {transaction.date}
                  </td>
                  <td style={{ 
                    padding: '8px', 
                    borderBottom: '1px solid #e5e7eb', 
                    fontWeight: '500' 
                  }}>
                    {transaction.senderCompany}
                  </td>
                  <td style={{ 
                    padding: '8px', 
                    borderBottom: '1px solid #e5e7eb', 
                    fontWeight: '500' 
                  }}>
                    {transaction.receiverCompany}
                  </td>
                  <td style={{ 
                    padding: '8px', 
                    borderBottom: '1px solid #e5e7eb', 
                    fontWeight: '600', 
                    color: '#059669' 
                  }}>
                    ${transaction.amount.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '9px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      background: transaction.status === 'SUCCESS' ? '#d1fae5' : 
                                 transaction.status === 'PENDING' ? '#fef3c7' : '#fee2e2',
                      color: transaction.status === 'SUCCESS' ? '#065f46' : 
                            transaction.status === 'PENDING' ? '#92400e' : '#991b1b'
                    }}>
                      {transaction.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Footer */}
        <div style={{ 
          marginTop: '30px', 
          textAlign: 'center', 
          color: '#6b7280', 
          fontSize: '10px', 
          borderTop: '1px solid #e5e7eb', 
          paddingTop: '20px' 
        }}>
          End of Report
        </div>
      </div>
    </div>
  );
};

export default TransactionHistoryPDF;
